// src/components/Sections/Admin/WaitingRoom/index.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Switch, InputNumber, Button, Table, Tag, Tooltip,
  Statistic, Alert, App, Divider, Typography, Badge, Segmented
} from 'antd';
import {
  ReloadOutlined, UserDeleteOutlined, ClearOutlined,
  DisconnectOutlined, ClockCircleOutlined, CheckCircleOutlined,
  PauseCircleOutlined, TeamOutlined, SettingOutlined,
  WarningOutlined, ThunderboltOutlined, DeleteOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import WaitingRoomAdmin from '../../../../service/WaitingRoomAdmin';
import { useNotify } from '../../../../providers/NotificationProvider';

const { Title, Text } = Typography;
const REFRESH_INTERVAL = 10000;

// Formatea minutos como "4h 20m" o "12 min"
const formatTiempo = (minutos) => {
  if (minutos === null || minutos === undefined || isNaN(minutos)) return '—';
  if (minutos < 1)   return '< 1 min';
  if (minutos < 60)  return `${minutos} min`;
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

// Formatea segundos como "2m 5s" o "45s"
const formatHB = (segundos) => {
  if (segundos === null || segundos === undefined || isNaN(segundos)) return '—';
  if (segundos < 60) return `${segundos}s`;
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return `${m}m ${s}s`;
};

const WaitingRoomDashboard = () => {
  const notify    = useNotify();
  const { modal } = App.useApp();

  const [stats, setStats]               = useState(null);
  const [usuarios, setUsuarios]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving]             = useState(false);
  const [maxSlotsInput, setMaxSlotsInput] = useState(100);
  const [timeoutInput, setTimeoutInput] = useState(15);
  const [lastUpdate, setLastUpdate]     = useState(null);
  const [filtro, setFiltro]             = useState('todos');
  const intervalRef = useRef(null);

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchStats = async () => {
    try {
      const res = await WaitingRoomAdmin.getStats();
      if (res?.data) {
        setStats(res.data);
        setMaxSlotsInput(res.data.maxSlots);
        setTimeoutInput(res.data.sessionTimeoutMinutes ?? 15);
      }
      setLastUpdate(new Date());
    } catch { /* silencioso en auto-refresh */ }
  };

  const fetchUsuarios = async (showSpinner = false) => {
    if (showSpinner) setLoadingUsers(true);
    try {
      const res = await WaitingRoomAdmin.getUsuarios();
      setUsuarios(res?.data || []);
    } catch {
      notify.error('Error al cargar usuarios');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchAll = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    await Promise.all([fetchStats(), fetchUsuarios()]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll(true);
    intervalRef.current = setInterval(() => fetchAll(false), REFRESH_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, []);

  // ── Acciones ────────────────────────────────────────────────────────────────
  const handleToggle = async (enabled) => {
    try {
      setSaving(true);
      const res = await WaitingRoomAdmin.setConfig({ enabled });
      if (res?.data) setStats(prev => ({ ...prev, ...res.data }));
      notify.success(enabled ? 'Sala de espera activada' : 'Sala de espera desactivada');
    } catch (e) { notify.error(e.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleSetMaxSlots = async () => {
    if (!maxSlotsInput || maxSlotsInput < 1) return;
    try {
      setSaving(true);
      const res = await WaitingRoomAdmin.setConfig({ maxSlots: maxSlotsInput });
      if (res?.data) setStats(prev => ({ ...prev, ...res.data }));
      notify.success(`Capacidad actualizada a ${maxSlotsInput} usuarios`);
    } catch (e) { notify.error(e.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleSetTimeout = async () => {
    if (!timeoutInput || timeoutInput < 1) return;
    try {
      setSaving(true);
      const res = await WaitingRoomAdmin.setConfig({ sessionTimeoutMinutes: timeoutInput });
      if (res?.data) setStats(prev => ({ ...prev, ...res.data }));
      notify.success(`Tiempo de sesion actualizado a ${timeoutInput} minutos`);
    } catch (e) { notify.error(e.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleLimpiarExpirados = () => {
    modal.confirm({
      title: 'Limpiar registros expirados',
      content: 'Se eliminarán todos los usuarios (admitidos y en espera) cuyo heartbeat lleva más de 2 minutos sin actualizarse. Esto no afecta a usuarios activos.',
      okText: 'Limpiar expirados', okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const res = await WaitingRoomAdmin.limpiarExpirados();
          notify.success(`${res?.data?.eliminados || 0} registros expirados eliminados`);
          fetchAll(false);
        } catch (e) { notify.error(e.message || 'Error'); }
      },
    });
  };

  const handleLimpiarCola = () => {
    modal.confirm({
      title: 'Limpiar cola de espera',
      content: 'Se eliminarán todos los usuarios con estado "en espera", independiente de su heartbeat.',
      okText: 'Limpiar cola', okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const res = await WaitingRoomAdmin.limpiarCola();
          notify.success(`Cola limpiada (${res?.data?.eliminados || 0} usuarios)`);
          fetchAll(false);
        } catch (e) { notify.error(e.message || 'Error'); }
      },
    });
  };

  const handleDesconectarTodos = () => {
    modal.confirm({
      title: '¿Desconectar a TODOS los usuarios?',
      content: 'Se eliminarán todos los registros. Cada usuario deberá volver a entrar a la cola.',
      okText: 'Desconectar todos', okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const res = await WaitingRoomAdmin.desconectarTodos();
          notify.success(`${res?.data?.eliminados || 0} usuarios desconectados`);
          fetchAll(false);
        } catch (e) { notify.error(e.message || 'Error'); }
      },
    });
  };

  const handleTogglePrioridad = async (campo, valor) => {
    try {
      setSaving(true);
      const res = await WaitingRoomAdmin.setConfig({ [campo]: valor });
      if (res?.data) setStats(prev => ({ ...prev, ...res.data }));
      const label = campo === 'prioridadAdultoMayor' ? 'Adulto mayor' : 'Tarjeta Vecino';
      notify.success(valor ? `Prioridad activada: ${label}` : `Prioridad desactivada: ${label}`);
    } catch (e) { notify.error(e.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleExpulsar = (userId) => {
    modal.confirm({
      title: 'Desconectar usuario',
      content: `¿Eliminar el registro de este usuario de la sala de espera?`,
      okText: 'Desconectar', okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await WaitingRoomAdmin.expulsar(userId);
          notify.success('Usuario desconectado');
          fetchAll(false);
        } catch (e) { notify.error(e.message || 'Error'); }
      },
    });
  };

  // ── Datos filtrados ─────────────────────────────────────────────────────────
  const usuariosFiltrados = usuarios.filter(u => {
    if (filtro === 'admitidos') return u.status === 'admitted';
    if (filtro === 'espera')    return u.status === 'waiting';
    return true;
  });

  const countAdmitidos = usuarios.filter(u => u.status === 'admitted').length;
  const countEspera    = usuarios.filter(u => u.status === 'waiting').length;

  // Usuarios con heartbeat expirado (> 2 min = 120s)
  const expirados = usuarios.filter(u => u.heartbeatHace > 120).length;

  const ocupacionColor = (pct) => {
    if (pct >= 90) return '#ef4444';
    if (pct >= 70) return '#f59e0b';
    return '#0047bb';
  };

  // ── Columnas ────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Nombre',
      key: 'nombre',
      ellipsis: true,
      render: (_, r) => r.nombre ? (
        <Tooltip title={`ID: ${r.userId}`}>
          <span className="text-sm font-medium text-slate-700">{r.nombre}</span>
        </Tooltip>
      ) : (
        <Tooltip title={r.userId}>
          <span className="font-mono text-xs text-slate-400">{r.userId.slice(0, 8)}…</span>
        </Tooltip>
      ),
    },
    {
      title: (
        <Tooltip title={
          <div>
            <p><strong>Admitido:</strong> El usuario está dentro de la plataforma y puede inscribirse en talleres.</p>
            <p><strong>En espera:</strong> El usuario está en cola esperando que se libere un cupo. Será admitido automáticamente cuando haya espacio.</p>
          </div>
        }>
          <span>Estado <QuestionCircleOutlined className="text-slate-400 text-xs" /></span>
        </Tooltip>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (v, r) => (
        <div className="flex flex-wrap gap-1 items-center">
          {v === 'admitted' ? (
            <Tooltip title="El usuario está dentro de la plataforma y puede inscribirse en talleres">
              <Tag icon={<CheckCircleOutlined />} color="blue">Admitido</Tag>
            </Tooltip>
          ) : (
            <Tooltip title="El usuario está en cola esperando que se libere un cupo. Será admitido automáticamente cuando haya espacio">
              <Tag icon={<ClockCircleOutlined />} color="warning">
                En espera {r.position ? `#${r.position}` : ''}
              </Tag>
            </Tooltip>
          )}
          {r.prioridad === 'adulto_mayor'   && <Tag color="purple" className="text-xs">🧓 Adulto mayor</Tag>}
          {r.prioridad === 'tarjeta_vecino' && <Tag color="blue"   className="text-xs">🪪 T. Vecino</Tag>}
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="Tiempo transcurrido desde que el usuario entró a la cola o fue admitido">
          <span>Tiempo en sala <QuestionCircleOutlined className="text-slate-400 text-xs" /></span>
        </Tooltip>
      ),
      dataIndex: 'tiempoEnSala',
      key: 'tiempoEnSala',
      width: 150,
      render: (v) => (
        <span className="text-xs text-slate-500">{formatTiempo(v)}</span>
      ),
    },
    {
      title: (
        <Tooltip title="Señal de vida del navegador. Si supera 2 minutos, el usuario probablemente cerró la pestaña sin desconectarse">
          <span>Último heartbeat <QuestionCircleOutlined className="text-slate-400 text-xs" /></span>
        </Tooltip>
      ),
      dataIndex: 'heartbeatHace',
      key: 'heartbeatHace',
      width: 170,
      sorter: (a, b) => (b.heartbeatHace || 0) - (a.heartbeatHace || 0),
      render: (v) => {
        const expirado = v > 120;
        return (
          <span className={`text-xs font-medium ${expirado ? 'text-red-500' : 'text-slate-500'}`}>
            hace {formatHB(v)} {expirado && '⚠️'}
          </span>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      render: (_, r) => (
        <Tooltip title="Desconectar usuario de la sala">
          <Button
            size="small" type="text" danger
            icon={<UserDeleteOutlined />}
            onClick={() => handleExpulsar(r.userId)}
          />
        </Tooltip>
      ),
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 md:p-6 space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Title level={4} className="!mb-0 flex items-center gap-2">
            <TeamOutlined className="text-blue-500" />
            Sala de Espera
          </Title>
          {lastUpdate && (
            <Text type="secondary" className="text-xs">
              Actualizado {dayjs(lastUpdate).format('HH:mm:ss')} · refresco cada 10s
            </Text>
          )}
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => fetchAll(true)} loading={loading}>
          Actualizar
        </Button>
      </div>

      {/* Config */}
      <Card size="small">
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-3">
            <SettingOutlined className="text-slate-400" />
            <Text strong className="text-sm">Estado</Text>
            <Switch
              checked={stats?.enabled ?? true}
              onChange={handleToggle}
              loading={saving}
              checkedChildren="Activa"
              unCheckedChildren="Inactiva"
            />
          </div>
          <Divider type="vertical" />
          <div className="flex items-center gap-2">
            <Text className="text-sm text-slate-600">Capacidad máxima:</Text>
            <InputNumber
              min={1} max={10000}
              value={maxSlotsInput}
              onChange={setMaxSlotsInput}
              style={{ width: 90 }}
              size="small"
            />
            <Button size="small" onClick={handleSetMaxSlots} loading={saving}>
              Guardar
            </Button>
          </div>
          <Divider type="vertical" />
          <div className="flex items-center gap-2">
            <Text className="text-sm text-slate-600">Tiempo de sesion (min):</Text>
            <InputNumber
              min={1} max={120}
              value={timeoutInput}
              onChange={setTimeoutInput}
              style={{ width: 70 }}
              size="small"
            />
            <Button size="small" onClick={handleSetTimeout} loading={saving}>
              Guardar
            </Button>
          </div>
          <Divider type="vertical" />
          <Tooltip title="Al activar, los usuarios detectados como adulto mayor (≥ 60 años) pasan automáticamente al frente de la cola de espera">
            <div className="flex items-center gap-2">
              <span className="text-sm">🧓</span>
              <Text className="text-sm text-slate-600">Prioridad adulto mayor</Text>
              <Switch
                size="small"
                checked={stats?.prioridadAdultoMayor ?? false}
                onChange={(v) => handleTogglePrioridad('prioridadAdultoMayor', v)}
                loading={saving}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
              {stats?.prioridadAdultoMayor && (
                <Tag color="purple" className="text-xs">
                  {usuarios.filter(u => u.status === 'waiting' && u.prioridad === 'adulto_mayor').length} en espera
                </Tag>
              )}
            </div>
          </Tooltip>
          <Divider type="vertical" />
          <Tooltip title="Al activar, los usuarios con Tarjeta Vecino vigente pasan automáticamente al frente de la cola de espera">
            <div className="flex items-center gap-2">
              <span className="text-sm">🪪</span>
              <Text className="text-sm text-slate-600">Prioridad Tarjeta Vecino</Text>
              <Switch
                size="small"
                checked={stats?.prioridadTarjetaVecino ?? false}
                onChange={(v) => handleTogglePrioridad('prioridadTarjetaVecino', v)}
                loading={saving}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
              {stats?.prioridadTarjetaVecino && (
                <Tag color="blue" className="text-xs">
                  {usuarios.filter(u => u.status === 'waiting' && u.prioridad === 'tarjeta_vecino').length} en espera
                </Tag>
              )}
            </div>
          </Tooltip>
        </div>

        {stats && !stats.enabled && (
          <Alert
            className="mt-3"
            message="La sala de espera está desactivada. Todos los usuarios son admitidos directamente."
            type="warning" icon={<PauseCircleOutlined />} showIcon
          />
        )}
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card size="small">
          <Statistic
            title="Admitidos"
            value={stats?.admitidos ?? '—'}
            suffix={stats ? `/ ${stats.maxSlots}` : ''}
            valueStyle={{ color: '#3b82f6', fontWeight: 700 }}
            prefix={<CheckCircleOutlined />}
          />
        </Card>
        <Card size="small">
          <Statistic
            title="En espera"
            value={stats?.enEspera ?? '—'}
            valueStyle={{ color: stats?.enEspera > 0 ? '#f59e0b' : '#0047bb', fontWeight: 700 }}
            prefix={<ClockCircleOutlined />}
          />
        </Card>
        <Card size="small">
          <Statistic
            title="Ocupación"
            value={stats?.ocupacion ?? '—'}
            suffix="%"
            valueStyle={{ color: ocupacionColor(stats?.ocupacion || 0), fontWeight: 700 }}
          />
          {stats && (
            <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(stats.ocupacion, 100)}%`,
                  backgroundColor: ocupacionColor(stats.ocupacion),
                }}
              />
            </div>
          )}
        </Card>
        <Card size="small">
          <Statistic
            title="Total conectados"
            value={(stats?.admitidos ?? 0) + (stats?.enEspera ?? 0)}
            valueStyle={{ fontWeight: 700 }}
            prefix={<TeamOutlined />}
          />
        </Card>
      </div>

      {/* Alertas de acción */}
      {expirados > 0 && (
        <Alert
          message={`${expirados} registro${expirados > 1 ? 's' : ''} con heartbeat expirado (> 2 min sin actividad)`}
          description="Estos usuarios probablemente cerraron el navegador sin desconectarse. Puedes limpiarlos manualmente."
          type="error"
          showIcon
          icon={<WarningOutlined />}
          action={
            <Button size="small" danger icon={<ThunderboltOutlined />} onClick={handleLimpiarExpirados}>
              Limpiar expirados
            </Button>
          }
        />
      )}

      {stats?.enEspera > 0 && (
        <Alert
          message={`${stats.enEspera} usuario${stats.enEspera > 1 ? 's' : ''} en cola de espera`}
          type="warning" showIcon
          action={
            <Button size="small" danger onClick={handleLimpiarCola}>
              Limpiar cola
            </Button>
          }
        />
      )}

      {/* Tabla */}
      <Card
        title={
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <TeamOutlined />
              Usuarios conectados
            </span>
            <Segmented
              size="small"
              value={filtro}
              onChange={setFiltro}
              options={[
                { label: `Todos (${usuarios.length})`,          value: 'todos' },
                { label: `Admitidos (${countAdmitidos})`,       value: 'admitidos' },
                { label: `En espera (${countEspera})`,          value: 'espera' },
              ]}
            />
          </div>
        }
        extra={
          <Button
            size="small" danger icon={<DisconnectOutlined />}
            onClick={handleDesconectarTodos}
            disabled={usuarios.length === 0}
          >
            Desconectar todos
          </Button>
        }
      >
        <Table
          dataSource={usuariosFiltrados}
          columns={columns}
          rowKey="userId"
          loading={loadingUsers}
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} usuarios` }}
          locale={{ emptyText: 'No hay usuarios en esta categoría' }}
          rowClassName={(r) => {
            if (r.heartbeatHace > 120)                return 'bg-red-50';
            if (r.status === 'waiting' && r.prioridad) return 'bg-purple-50';
            if (r.status === 'waiting')                return 'bg-amber-50';
            return '';
          }}
        />
      </Card>
    </div>
  );
};

export default WaitingRoomDashboard;
